import { Button, Modal } from '@ksyos/design-system';
import { useState } from 'react';
import type { SortDirection } from '@frontend/utils/sortDirection';
import { ManagerAuthorization } from '@common/enums';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization } from '@frontend/utils';
import type { IEmployment } from '../../../../interfaces/employment';
import type { IProfile } from '../steps/AssignRoles';
import { BadgeWithLabel } from '../../../../elements/BadgeWithLabel';
import { ListPager } from '../../../../elements/ListPager';
import { AddEmploymentsTable } from '../components/AddEmploymentsTable';

interface IAddEmploymentsToRoleModalProps {
    employments: IEmployment[];
    onClose: () => void;
    onSortEmployments: (sortKey: string, sortDirection: SortDirection) => Promise<void>;
    saveSelectedEmployments: (newSelectedEmployments: string[]) => Promise<void>;
    selectedEmploymentIds: string[];
    selectedProfile: IProfile;
    sortDirection: SortDirection;
}

export function AddEmploymentsToRoleModal(props: IAddEmploymentsToRoleModalProps) {
    const [selectedEmploymentIds, setSelectedEmploymentIds] = useState(props.selectedEmploymentIds);
    const [page, setPage] = useState(1);
    const itemsPerPage = 13;
    assertHasAuthorization(ManagerAuthorization.organizationAddEdit);

    return (
        <Modal
            isOpen
            onClose={props.onClose}
            size="large"
        >
            <Modal.Header>{`${props.selectedProfile.name} ${translateKey('common.role')}`}</Modal.Header>
            <Modal.CloseButton />
            <Modal.Body>
                <AddEmploymentsTable
                    employments={getEmploymentsByPage()}
                    onChange={updateSelectedEmploymentIds}
                    onSort={props.onSortEmployments}
                    selectedEmployments={selectedEmploymentIds}
                    sortDirection={props.sortDirection}
                />
                <ListPager
                    itemsPerPage={itemsPerPage}
                    onChangePage={changePage}
                    page={page}
                    total={props.employments.length}
                />
            </Modal.Body>
            <Modal.Footer>
                <BadgeWithLabel
                    color="neutral"
                    label={translateKey('common.isSelected')}
                    value={selectedEmploymentIds.length.toString()}
                />
                <Button
                    onClick={() => props.saveSelectedEmployments(selectedEmploymentIds)}
                    variant="solid"
                >
                    {translateKey('common.saveButton')}
                </Button>
            </Modal.Footer>
        </Modal>
    );

    async function updateSelectedEmploymentIds(selectedRowKeys: string[]) {
        setSelectedEmploymentIds(selectedRowKeys);
    }

    async function changePage(newPageNumber: number) {
        setPage(newPageNumber);
    }

    function getEmploymentsByPage() {
        const start = (page - 1) * itemsPerPage;
        const end = start + itemsPerPage;

        return props.employments.slice(start, end);
    }
}
